import React, { useState, useEffect } from 'react';
import { 
    Card, 
    List, 
    Avatar, 
    Typography, 
    Button, 
    Tag, 
    Input,
    Select,
    Row,
    Col,
    Spin,
    Empty,
    message,
    Progress,
    Space,
    DatePicker,
    Modal,
    Tooltip
} from 'antd';
import { 
    BookOutlined, 
    ClockCircleOutlined, 
    SearchOutlined,
    EyeOutlined,
    DeleteOutlined,
    CalendarOutlined,
    StarOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getBaseURL } from '../config.js';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { confirm } = Modal;

const LearningHistory = () => {
    const navigate = useNavigate();
    const [historyData, setHistoryData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [dateRange, setDateRange] = useState(null);
    const [statistics, setStatistics] = useState({});
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });
    const baseURL = getBaseURL();

    useEffect(() => {
        fetchLearningHistory();
    }, [pagination.current, pagination.pageSize, searchText, filterStatus, dateRange]);

    const fetchLearningHistory = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams({
                page: pagination.current.toString(),
                limit: pagination.pageSize.toString()
            });

            if (searchText) {
                params.append('search', searchText);
            }
            if (filterStatus !== 'all') {
                params.append('status', filterStatus);
            }
            if (dateRange && dateRange.length === 2) {
                params.append('start_date', dateRange[0].format('YYYY-MM-DD'));
                params.append('end_date', dateRange[1].format('YYYY-MM-DD'));
            }

            const response = await axios.get(`https://${baseURL}/user/learning-history?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                const { records, pagination: paginationData, statistics: statsData } = response.data.data;
                setHistoryData(records);
                setStatistics(statsData);
                setPagination(prev => ({
                    ...prev,
                    total: paginationData.total_records
                }));
            } else {
                message.error(response.data.message || 'Failed to load learning history');
            }
        } catch (error) {
            console.error('Error fetching learning history:', error);
            if (error.response?.status === 401) {
                localStorage.removeItem('token');
                navigate('/login');
            } else {
                message.error('Failed to load learning history');
            }
        } finally {
            setLoading(false);
        }
    };

    const filterHistory = () => {
        let filtered = [...historyData];

        // 文本搜索
        if (searchText) {
            filtered = filtered.filter(item => 
                item.course_title?.toLowerCase().includes(searchText.toLowerCase()) ||
                item.content?.toLowerCase().includes(searchText.toLowerCase())
            );
        }

        // 状态过滤
        if (filterStatus !== 'all') {
            filtered = filtered.filter(item => {
                switch (filterStatus) {
                    case 'completed':
                        return item.progress >= 100;
                    case 'in-progress':
                        return item.progress > 0 && item.progress < 100;
                    case 'not-started':
                        return item.progress === 0;
                    case 'saved':
                        return item.saved;
                    default:
                        return true;
                }
            });
        }

        // 日期范围过滤
        if (dateRange && dateRange.length === 2) {
            filtered = filtered.filter(item => {
                const itemDate = dayjs(item.lastAccessed);
                return itemDate.isAfter(dateRange[0]) && itemDate.isBefore(dateRange[1]);
            });
        }

        setFilteredData(filtered);
    };

    const handleViewCourse = (courseId, sessionId) => {
        if (sessionId) {
            navigate(`/course/${courseId}?session=${sessionId}`);
        } else {
            navigate(`/course/${courseId}`);
        }
    };

    const handleDeleteHistory = async (historyId) => {
        confirm({
            title: 'Delete Learning Record',
            content: 'Are you sure you want to delete this learning record?',
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    const token = localStorage.getItem('token');
                    const response = await axios.delete(`https://${baseURL}/user/learning-history/${historyId}`, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    });

                    if (response.data.success) {
                        message.success('Learning record deleted successfully');
                        fetchLearningHistory(); // 重新获取数据
                    } else {
                        message.error(response.data.message || 'Failed to delete learning record');
                    }
                } catch (error) {
                    console.error('Error deleting learning record:', error);
                    if (error.response?.data?.error) {
                        message.error(error.response.data.message || 'Failed to delete learning record');
                    } else {
                        message.error('Network error. Please try again.');
                    }
                }
            }
        });
    };

    const getStatusColor = (progress) => {
        if (progress >= 100) return 'success';
        if (progress > 0) return 'processing';
        return 'default';
    };

    const getStatusText = (progress) => {
        if (progress >= 100) return 'Completed';
        if (progress > 0) return 'In Progress';
        return 'Not Started';
    };

    const formatStudyTime = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        
        if (hours > 0) {
            return `${hours}h ${minutes}m`;
        }
        return `${minutes}m`;
    };

    if (loading) {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '60vh' 
            }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div style={{ 
            padding: '24px',
            background: '#f5f5f5',
            minHeight: 'calc(100vh - 64px)'
        }}>
            <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                <Title level={2} style={{ marginBottom: 24 }}>Learning History</Title>

                {/* 搜索和过滤器 */}
                <Card style={{ marginBottom: 24, borderRadius: 12 }}>
                    <Row gutter={[16, 16]} align="middle">
                        <Col xs={24} sm={12} md={8}>
                            <Input
                                placeholder="Search courses or content..."
                                prefix={<SearchOutlined />}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                allowClear
                            />
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Select
                                placeholder="Filter by status"
                                value={filterStatus}
                                onChange={setFilterStatus}
                                style={{ width: '100%' }}
                            >
                                <Option value="all">All Status</Option>
                                <Option value="completed">Completed</Option>
                                <Option value="in-progress">In Progress</Option>
                                <Option value="not-started">Not Started</Option>
                                <Option value="saved">Saved</Option>
                            </Select>
                        </Col>
                        <Col xs={24} sm={24} md={10}>
                            <RangePicker
                                placeholder={['Start Date', 'End Date']}
                                value={dateRange}
                                onChange={setDateRange}
                                style={{ width: '100%' }}
                            />
                        </Col>
                    </Row>
                </Card>

                {/* 统计信息 */}
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col xs={12} sm={6}>
                        <Card style={{ textAlign: 'center', borderRadius: 12 }}>
                            <Text type="secondary">Total Courses</Text>
                            <div style={{ fontSize: 24, fontWeight: 'bold', color: '#1890ff' }}>
                                {historyData.length}
                            </div>
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card style={{ textAlign: 'center', borderRadius: 12 }}>
                            <Text type="secondary">Completed</Text>
                            <div style={{ fontSize: 24, fontWeight: 'bold', color: '#52c41a' }}>
                                {historyData.filter(item => item.progress >= 100).length}
                            </div>
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card style={{ textAlign: 'center', borderRadius: 12 }}>
                            <Text type="secondary">In Progress</Text>
                            <div style={{ fontSize: 24, fontWeight: 'bold', color: '#faad14' }}>
                                {historyData.filter(item => item.progress > 0 && item.progress < 100).length}
                            </div>
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card style={{ textAlign: 'center', borderRadius: 12 }}>
                            <Text type="secondary">Saved Items</Text>
                            <div style={{ fontSize: 24, fontWeight: 'bold', color: '#722ed1' }}>
                                {historyData.filter(item => item.saved).length}
                            </div>
                        </Card>
                    </Col>
                </Row>

                {/* 历史记录列表 */}
                <Card style={{ borderRadius: 12 }}>
                    {filteredData.length > 0 ? (
                        <List
                            itemLayout="vertical"
                            size="large"
                            pagination={{
                                pageSize: 10,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total, range) => 
                                    `${range[0]}-${range[1]} of ${total} items`
                            }}
                            dataSource={filteredData}
                            renderItem={(item) => (
                                <List.Item
                                    key={item.id}
                                    actions={[
                                        <Button 
                                            type="primary" 
                                            icon={<EyeOutlined />}
                                            onClick={() => handleViewCourse(item.courseid, item.sessionId)}
                                        >
                                            View
                                        </Button>,
                                        <Button 
                                            danger 
                                            icon={<DeleteOutlined />}
                                            onClick={() => handleDeleteHistory(item.id)}
                                        >
                                            Delete
                                        </Button>
                                    ]}
                                    extra={
                                        <div style={{ width: 200 }}>
                                            <Progress 
                                                percent={item.progress || 0} 
                                                size="small"
                                                status={getStatusColor(item.progress)}
                                            />
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                Progress: {item.progress || 0}%
                                            </Text>
                                        </div>
                                    }
                                >
                                    <List.Item.Meta
                                        avatar={
                                            <Avatar 
                                                size={64}
                                                icon={<BookOutlined />} 
                                                style={{ backgroundColor: '#1890ff' }}
                                            />
                                        }
                                        title={
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <Text strong style={{ fontSize: 16 }}>
                                                    {item.course_title || `Course ${item.courseid}`}
                                                </Text>
                                                <Tag color={getStatusColor(item.progress)}>
                                                    {getStatusText(item.progress)}
                                                </Tag>
                                                {item.saved && (
                                                    <Tag color="purple" icon={<StarOutlined />}>
                                                        Saved
                                                    </Tag>
                                                )}
                                            </div>
                                        }
                                        description={
                                            <Space direction="vertical" style={{ width: '100%' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                                    <Text type="secondary">
                                                        <CalendarOutlined /> Last accessed: {' '}
                                                        {dayjs(item.lastAccessed).format('YYYY-MM-DD HH:mm')}
                                                    </Text>
                                                    <Text type="secondary">
                                                        <ClockCircleOutlined /> Study time: {formatStudyTime(item.studyTime || 0)}
                                                    </Text>
                                                </div>
                                                {item.preferences && (
                                                    <div>
                                                        <Tag color="blue">
                                                            Language: {item.preferences.language}
                                                        </Tag>
                                                        <Tag color="green">
                                                            Depth: {item.preferences.depth}
                                                        </Tag>
                                                    </div>
                                                )}
                                            </Space>
                                        }
                                    />
                                    {item.content && (
                                        <Paragraph 
                                            ellipsis={{ rows: 2, expandable: true }}
                                            style={{ marginTop: 16, color: '#666' }}
                                        >
                                            {item.content}
                                        </Paragraph>
                                    )}
                                </List.Item>
                            )}
                        />
                    ) : (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                <span>
                                    {searchText || filterStatus !== 'all' || dateRange 
                                        ? 'No matching history found' 
                                        : 'No learning history yet'
                                    }
                                </span>
                            }
                        >
                            {!searchText && filterStatus === 'all' && !dateRange && (
                                <Button 
                                    type="primary" 
                                    onClick={() => navigate('/course-selection')}
                                >
                                    Start Learning
                                </Button>
                            )}
                        </Empty>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default LearningHistory; 